/**
 * Conversation — what a visitor actually came for.
 *
 * Sean, looking at `/rehearsal`: **"why isn't it just like a chat?"** It
 * should be, and it was not, because the demo was mounted on the SDK
 * developer console — a visitor inherited a producer's tool, and spent the
 * first twenty of their ninety seconds working out that the score editor
 * was not for them.
 *
 * ## The trap in "just like a chat"
 *
 * A literal chat is the forbidden default wearing different clothes.
 * `.claude/design-context.md` bans the SaaS console palette on the grounds
 * that nobody in this category owns the paper-on-graphite inversion, and a
 * bubble layout gives up the one thing that makes this surface look like
 * nothing else. So the question the direction loop asked was not "chat or
 * not chat" but **what artifact is a conversation**, and the answer it
 * reached is a *script page*: the format invented for two people talking,
 * and native to a rehearsal studio.
 *
 * ## Run-in names, and why
 *
 * The name sits at the head of the line rather than above it or in a margin
 * column. Above it — the first direction — reads beautifully and spends a
 * whole line per turn, so six turns are already scrolling and beat 3 needs
 * more than six. In a margin column — the second — is legible without being
 * read, which is genuinely valuable, and is also structurally the take log
 * this replaced. Run-in is how printed play texts have set dialogue for four
 * centuries, for exactly this reason.
 *
 * ## What is deliberately absent
 *
 * No timestamps, no take id, no session id, no auto-scroll control, no
 * second button. Every one of those was on the surface a recruiter saw, and
 * `docs/evidence/beat0-viewport-legibility.md` records what the first of
 * them cost: a microsecond-precision ISO instant took 240px of every row and
 * left Margo's greeting fifteen pixels.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from "react";

import { conversationCopy as copy } from "./Conversation.copy";

export type TurnSpeaker = "character" | "visitor" | "room";

export interface Turn {
  readonly id: string;
  readonly speaker: TurnSpeaker;
  /** Display name. Absent for `room`, which speaks as nobody. */
  readonly name?: string;
  readonly text: string;
  /** A character mid-sentence; the line grows as tokens arrive. */
  readonly partial?: boolean;
  /**
   * Not something anyone said: a stage direction about the turn (#153).
   *
   * Set only for a `no_reply` — the character was asked and the line did
   * not come. It keeps the name, because *whose* turn failed to arrive
   * is the whole of what a visitor learns from it, and it loses the
   * voice of dialogue, because she did not say this and must not appear
   * to have chosen the silence.
   *
   * `not_heard` is a `room` turn instead and needs no flag: that
   * register is already unattributed and italic, which is exactly the
   * claim being made about a microphone that produced nothing.
   */
  readonly stageDirection?: boolean;
}

export type ConversationStatus = "live" | "connecting" | "lost";

export interface RoomMember {
  readonly voiceId: string;
  readonly name: string;
}

export interface ConversationProps {
  readonly turns: readonly Turn[];
  readonly status?: ConversationStatus;
  readonly onSend: (text: string) => void;
  /** A character is composing; the field stays live regardless. */
  readonly thinking?: boolean;
  /**
   * Who is composing.
   *
   * Rendered in the run-in name slot so the wait occupies the shape her
   * line will occupy. Without it the state was a bare ellipsis at the far
   * left, which reads as a stray mark rather than as someone thinking --
   * and a visitor waiting several seconds for a reply needs to know it is
   * *her* they are waiting on.
   */
  readonly thinkingName?: string;
  readonly disabled?: boolean;

  /**
   * Who is in the room, who is being spoken to, and how to change either.
   *
   * Beat 1 is a visitor putting someone new in the room with one sentence,
   * and beat 3 is telling that person something and asking the first about
   * it. Neither was reachable: `addressed` was set once to whoever arrived
   * first, and the only controls for both lived on the developer console.
   * Found by trying to drive the arc past beat 2 rather than by any test.
   */
  readonly room?: readonly RoomMember[];
  readonly addressed?: string;
  readonly onAddress?: (voiceId: string) => void;
  readonly onBringSomeoneIn?: (sentence: string) => void;
  readonly bringingSomeoneIn?: boolean;
  readonly bringError?: string | null;
  readonly roomFull?: boolean;
}

/** Read live, so a viewer who changes the setting is believed without a reload. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const q = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (): void => setReduced(q.matches);
    q.addEventListener("change", onChange);
    return () => q.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

/**
 * The width the speaker name occupies before the line begins.
 *
 * Fixed rather than intrinsic, because it is doing two jobs. It gives the
 * hanging indent below something to hang from, and it puts MARGO's text
 * and YOU's text at the same left edge — which turns the run-in names into
 * a scannable column without the margin column that made the second
 * direction structurally identical to the take log it replaced.
 */
const NAME_COLUMN = "4.75rem";

const NAME: React.CSSProperties = {
  fontFamily: "var(--font-sohne-display)",
  fontSize: "0.688rem",
  letterSpacing: "0.13em",
  textTransform: "uppercase",
  // Not a colon, not a bracket. A script page separates the name from the
  // line with space and weight, and anything else reads as a log prefix.
  display: "inline-block",
  width: NAME_COLUMN,
  whiteSpace: "nowrap",
  /*
    `text-indent` is inherited, and the paragraph sets a negative one for
    the hanging indent. Without resetting it here the name inherits the
    -4.75rem and applies it to its own first line, so the span's box sits
    correctly and its glyphs paint outside it, off the left edge.
    `getBoundingClientRect` reported the box at the right x throughout --
    the measurement was of the box and the defect was in the paint, which
    is only visible by looking.
  */
  textIndent: 0,
};

export function Conversation({
  turns,
  status = "live",
  onSend,
  thinking = false,
  thinkingName,
  disabled = false,
  room = [],
  addressed,
  onAddress,
  onBringSomeoneIn,
  bringingSomeoneIn = false,
  bringError = null,
  roomFull = false,
}: ConversationProps): ReactElement {
  const [draft, setDraft] = useState("");
  /**
   * The field describes someone instead of speaking to them.
   *
   * A mode rather than a second field, because #66's promise was one place
   * to type: beat 1 is one sentence, so the sentence goes where sentences
   * go. It clears itself when the cast comes back.
   */
  const [describing, setDescribing] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const reduced = usePrefersReducedMotion();

  // Follows the conversation without a control for it. The take log had a
  // visible "auto-scroll: on" toggle; a visitor has no use for the setting
  // and no idea what it means, and it was one more thing to dismiss.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: reduced ? "auto" : "smooth" });
  }, [turns, thinking, reduced]);

  const submit = useCallback((): void => {
    const text = draft.trim();
    if (!text) return;
    if (describing && onBringSomeoneIn) {
      onBringSomeoneIn(text);
    } else {
      onSend(text);
    }
    setDraft("");
  }, [draft, describing, onBringSomeoneIn, onSend]);

  // The room answered; the field goes back to talking.
  useEffect(() => {
    if (!bringingSomeoneIn) setDescribing(false);
  }, [bringingSomeoneIn]);

  const addressedName = room.find((m) => m.voiceId === addressed)?.name;
  const canBring = onBringSomeoneIn !== undefined && !roomFull;

  const sendDisabled = disabled || draft.trim().length === 0;

  return (
    <section
      data-testid="conversation"
      data-status={status}
      aria-label="The room"
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        background: "var(--stage-riser)",
        border: "1px solid var(--ledger-line)",
        borderRadius: "var(--radius)",
        boxShadow: "var(--shadow-lamp-warm)",
        overflow: "hidden",
      }}
    >
      <div
        ref={scrollRef}
        data-testid="conversation-scroll"
        aria-live="polite"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "1.75rem 1.875rem 1.25rem",
          fontFamily: "var(--font-sohne-prose)",
        }}
      >
        {turns.length === 0 ? (
          <p
            data-testid="conversation-empty"
            style={{
              margin: 0,
              fontSize: "0.938rem",
              color: "var(--paper-cream-faint)",
              fontStyle: "italic",
            }}
          >
            {status === "connecting" ? copy.states.connecting : copy.waiting}
          </p>
        ) : (
          <div style={{ maxWidth: "62ch" }}>
            {turns.map((turn) => (
              <p
                key={turn.id}
                className="conversation-turn"
                data-testid={`turn-${turn.id}`}
                data-speaker={turn.speaker}
                /*
                  Present only on a stage direction, so a test can assert
                  the *absence* of the treatment on an ordinary reply.
                  That direction is the one that matters here: the bar
                  was that "Mm." must still read as a reply, and a check
                  can only prove that by having something to look for.
                */
                data-stage-direction={turn.stageDirection ? "true" : undefined}
                style={{
                  margin: "0 0 1.05rem",
                  fontSize: "1rem",
                  lineHeight: 1.62,
                  /*
                    A hanging indent, which is what makes a run-in name
                    readable rather than merely compact. Without it the
                    second line of a turn returns to the far left, sitting
                    under the speaker name, and a continuation becomes
                    indistinguishable from a new speaker at a glance. In
                    situ that was the single worst thing on the surface.
                    Every line after the first now aligns to the text edge,
                    which is how a printed play text has always set it.
                  */
                  paddingLeft: turn.speaker === "room" ? 0 : NAME_COLUMN,
                  textIndent: turn.speaker === "room" ? 0 : `-${NAME_COLUMN}`,
                  color:
                    turn.speaker === "visitor"
                      ? "var(--paper-cream-dim)"
                      : turn.speaker === "room" || turn.stageDirection
                        ? "var(--paper-cream-faint)"
                        : "var(--paper-cream)",
                  fontStyle:
                    turn.speaker === "room" || turn.stageDirection
                      ? "italic"
                      : undefined,
                }}
              >
                {turn.speaker !== "room" && (
                  <span
                    data-testid={`turn-name-${turn.id}`}
                    style={{
                      ...NAME,
                      color:
                        turn.speaker === "visitor"
                          ? "var(--paper-cream-faint)"
                          : /*
                              Not brass on a direction, because brass is
                              how this surface says someone spoke. The
                              name still carries the information — whose
                              turn did not arrive — and setting it in the
                              same recessive weight as the direction
                              makes the whole line one register: about
                              her, not from her.

                              `--brass-tarnish` was the first choice and
                              is measurably wrong: 1.76:1 against
                              `--stage-riser`, where the design context
                              makes AA a requirement, on the one word
                              that carries the line's meaning. Found by
                              measuring the rendered page, not by
                              looking — it reads as dim-but-legible, and
                              that is exactly what 1.76:1 looks like to
                              someone who already knows what it says.
                              `--paper-cream-faint` is 4.80:1 there.

                              Four other sites use the same token as text
                              and fail the same way, including the
                              thinking name forty lines below and the
                              "bring someone in" button in this file.
                              They are animachord#156 rather than this
                              lane, because which token replaces each is
                              a per-surface design call and
                              `--brass-bezel`, the obvious answer, also
                              fails on three of four grounds.
                            */
                            turn.stageDirection
                            ? "var(--paper-cream-faint)"
                            : "var(--brass-cue)",
                    }}
                  >
                    {turn.speaker === "visitor" ? copy.you : turn.name}
                  </span>
                )}
                {/*
                  Brackets are markup and are hidden from assistive
                  technology: they are how print sets a stage direction,
                  and read aloud they are noise in front of the only
                  sentence a listener gets. The italic and the tarnished
                  name carry the register for a reader; the words carry
                  it for a listener, which is why the copy has to make
                  sense unbracketed and does.
                */}
                {turn.stageDirection && <span aria-hidden>[</span>}
                {turn.text}
                {turn.stageDirection && <span aria-hidden>]</span>}
              </p>
            ))}
            {thinking && (
              <p
                className="conversation-turn"
                data-testid="conversation-thinking"
                /*
                  Announced rather than hidden. A visitor using a screen
                  reader has no other signal that anyone is composing, and
                  `aria-hidden` on the only such signal is the accessible
                  equivalent of a silent page.
                */
                aria-label={
                  thinkingName ? `${thinkingName} is thinking` : "Thinking"
                }
                style={{
                  margin: 0,
                  fontSize: "1rem",
                  lineHeight: 1.62,
                  paddingLeft: NAME_COLUMN,
                  textIndent: `-${NAME_COLUMN}`,
                  color: "var(--paper-cream-faint)",
                }}
              >
                {thinkingName && (
                  <span style={{ ...NAME, color: "var(--brass-tarnish)" }}>
                    {thinkingName}
                  </span>
                )}
                <span
                  aria-hidden
                  className={reduced ? undefined : "conversation-thinking-mark"}
                  style={{ letterSpacing: "0.2em" }}
                >
                  {copy.states.thinking}
                </span>
              </p>
            )}
          </div>
        )}
      </div>

      {status === "lost" && (
        <p
          data-testid="conversation-status"
          role="status"
          style={{
            margin: 0,
            padding: "0.5rem 1.875rem",
            fontSize: "0.781rem",
            color: "var(--dynamic-forte)",
            borderTop: "1px solid var(--measure-line)",
          }}
        >
          {copy.states.lost}
        </p>
      )}

      {/*
        One field and one action. The surface this replaces had a `send`
        and a heavier-weighted `send cue` beside it (#58), so the primary
        action was not the one a visitor wanted. Beat 1 -- putting a new
        character in the room -- is one sentence, so it goes through this
        same field rather than earning a second control.
      */}
      {/*
        Who is in the room, and the one thing a visitor can do besides
        talk. One line, no borders, no buttons -- the direction loop's
        other candidate put the room in a sentence, which read better and
        made *who you are speaking to* much harder to see. The lit lamp
        carries the state; the placeholder says the same thing again in
        words, because this is the one piece of state a visitor must not
        get wrong.
      */}
      {(room.length > 1 || canBring) && (
        <div
          data-testid="room-row"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "1rem",
            padding: "0.75rem 1.875rem 0",
            borderTop: "1px solid var(--ledger-line)",
            background: "var(--stage-pit)",
            fontSize: "0.813rem",
          }}
        >
          {room.map((member) => {
            const lit = member.voiceId === addressed;
            return (
              <button
                key={member.voiceId}
                type="button"
                data-testid={`room-${member.voiceId}`}
                data-lit={lit}
                aria-pressed={lit}
                aria-label={`Speak to ${member.name}`}
                disabled={disabled || describing}
                onClick={(): void => onAddress?.(member.voiceId)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  background: "none",
                  border: "none",
                  padding: 0,
                  fontFamily: "var(--font-sohne-prose)",
                  fontSize: "0.813rem",
                  color: lit ? "var(--paper-cream)" : "var(--paper-cream-faint)",
                  cursor: disabled || describing ? "default" : "pointer",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: "0.44rem",
                    height: "0.44rem",
                    borderRadius: "50%",
                    background: lit ? "var(--brass-cue)" : "var(--rehearsal-edge)",
                    boxShadow: lit ? "0 0 8px var(--lamp-pool)" : "none",
                  }}
                />
                {member.name}
              </button>
            );
          })}

          {canBring && !describing && (
            <button
              type="button"
              data-testid="bring-someone-in"
              onClick={(): void => setDescribing(true)}
              disabled={disabled || bringingSomeoneIn}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                fontFamily: "var(--font-sohne-prose)",
                fontSize: "0.813rem",
                color: "var(--brass-tarnish)",
                cursor: "pointer",
              }}
            >
              {bringingSomeoneIn ? copy.bringing : copy.bringSomeoneIn}
            </button>
          )}

          {describing && (
            <button
              type="button"
              data-testid="bring-cancel"
              onClick={(): void => setDescribing(false)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                fontFamily: "var(--font-sohne-prose)",
                fontSize: "0.813rem",
                color: "var(--paper-cream-faint)",
                cursor: "pointer",
              }}
            >
              {copy.describeCancel}
            </button>
          )}

          {roomFull && (
            <span
              data-testid="room-full"
              style={{ color: "var(--paper-cream-faint)" }}
            >
              {copy.castFull}
            </span>
          )}

          {bringError && (
            <span
              data-testid="bring-error"
              role="status"
              style={{ color: "var(--dynamic-forte)" }}
            >
              {bringError}
            </span>
          )}
        </div>
      )}

      <form
        data-testid="composer"
        onSubmit={(event): void => {
          event.preventDefault();
          submit();
        }}
        style={{
          display: "flex",
          gap: "0.625rem",
          padding: "0.875rem 1.875rem 1.125rem",
          borderTop:
            room.length > 1 || canBring ? "none" : "1px solid var(--ledger-line)",
          background: "var(--stage-pit)",
        }}
      >
        <input
          type="text"
          data-testid="composer-input"
          aria-label={
            describing
              ? copy.describePlaceholder
              : addressedName
                ? copy.placeholderTo(addressedName)
                : copy.placeholder
          }
          placeholder={
            describing
              ? copy.describePlaceholder
              : addressedName && room.length > 1
                ? copy.placeholderTo(addressedName)
                : copy.placeholder
          }
          value={draft}
          disabled={disabled}
          onChange={(event): void => setDraft(event.target.value)}
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            color: "var(--paper-cream)",
            border: "none",
            borderBottom: "1px solid var(--measure-line)",
            padding: "0.45rem 0.1rem",
            fontFamily: "var(--font-sohne-prose)",
            fontSize: "1rem",
            outline: "none",
          }}
        />
        <button
          type="submit"
          data-testid="composer-send"
          disabled={sendDisabled}
          style={{
            /*
              Still a button when there is nothing to send. Transparent on
              transparent read as not-a-button, and an empty field is the
              first state a visitor sees — so the one affordance on the
              surface was invisible at exactly the moment they were looking
              for it. Dimmer brass, not absent brass.
            */
            background: sendDisabled ? "var(--rehearsal-taupe)" : "var(--brass-cue)",
            color: sendDisabled ? "var(--paper-cream-dim)" : "var(--score-ink)",
            border: `1px solid ${sendDisabled ? "var(--brass-tarnish)" : "var(--brass-bezel)"}`,
            borderRadius: "var(--radius)",
            padding: "0.45rem 1.1rem",
            fontFamily: "var(--font-sohne-prose)",
            fontSize: "0.875rem",
            cursor: sendDisabled ? "default" : "pointer",
            transition: reduced ? "none" : "background 160ms ease-out",
          }}
        >
          {copy.send}
        </button>
      </form>
    </section>
  );
}

export default Conversation;
